import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import DateSelect from "@/components/DateSelect";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import { DateTime } from "luxon";
import Banner from "@/components/Banner";
import { toDateFormat, toDateOnlyInTimezone } from "@/lib/dateHelpers";

export interface RenewJobPostModalProps {
  disabled: boolean;
  open: boolean;
  setOpen: (value: boolean) => void;
  handleRenew: (updatedDeadline: string) => void;
  currentSubmissionDeadline?: string;
}

const SUBMISSION_DEADLINE_ZONE = "America/New_York";

export default function RenewJobPostModal({
  disabled,
  open,
  setOpen,
  handleRenew,
  currentSubmissionDeadline
}: RenewJobPostModalProps) {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      updatedDeadline: { startDate: null, endDate: null }
    }
  });

  useEffect(() => {
    if (!open) {
      setValue("updatedDeadline", { startDate: null, endDate: null });
    }
  }, [open, setValue]);

  const formatCurrentDeadline = (deadline: string) => {
    if (!deadline) return "Not set";

    const normalizedDeadline = toDateOnlyInTimezone(deadline, SUBMISSION_DEADLINE_ZONE);
    return normalizedDeadline
      ? (toDateFormat(normalizedDeadline, { format: "MM/dd/yyyy" }) ?? "Invalid date")
      : "Invalid date";
  };

  const onSubmit = (data: { updatedDeadline: { startDate: string | null; endDate: string | null } }) => {
    if (data.updatedDeadline.startDate) {
      handleRenew(data.updatedDeadline.startDate);
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={() => {
        setOpen(false);
      }}
      headline="Renew Job Post"
      className="w-[520px] p-6 pr-2 max-h-[80vh]"
      displayOverflow
      actions={
        <>
          <Button
            variant="text"
            onClick={() => {
              setOpen(false);
            }}
            disabled={disabled}
          >
            Cancel
          </Button>

          <Button
            trailingIcon={false}
            icon={disabled ? "progress_activity" : undefined}
            iconClassName={disabled ? "animate-spin w-6 h-6 !block" : ""}
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
          >
            Renew Job Post
          </Button>
        </>
      }
      hasFormElements
    >
      <div className="space-y-6">
        <div>
          Are you sure you want to renew this job post on the job board? The job will be shown back at the top of the
          job board for increased visibility.
        </div>

        <Banner
          inline
          variant="info"
          description={`Current Submission Deadline: ${formatCurrentDeadline(currentSubmissionDeadline || "")}`}
        />

        <Controller
          name="updatedDeadline"
          control={control}
          rules={{
            validate: {
              hasStartDate: (value) => {
                if (!value?.startDate) {
                  return "Updated submission deadline is required";
                }
                return true;
              }
            }
          }}
          render={({ field: { onChange, value } }) => (
            <DateSelect
              label="Updated Submission Deadline *"
              value={value}
              useModal={true}
              onChange={onChange}
              useSingleDate
              minDate={DateTime.now().plus({ days: 1 }).startOf("day").toJSDate()}
              displayFormat="MMM DD, YYYY"
              showFooter={false}
              error={!!errors.updatedDeadline}
              errorMessage={errors.updatedDeadline?.message}
            />
          )}
        />
      </div>
    </Dialog>
  );
}
